/**
 * Copy-to-clipboard with a fallback — used by §F5.10's Share button and, later, by §F8's
 * "copy citation" and the §F5.10 export modal's job links.
 *
 * `navigator.clipboard` is not always there: it is undefined on an insecure origin (the
 * stack is reached over plain HTTP at `http://localhost:8090` in dev and on any LAN host
 * that has no certificate yet), and `writeText` rejects when the document is not focused
 * or the user denied the permission. The `execCommand('copy')` path still works in every
 * browser the §I5 baseline names, so the button keeps working rather than silently doing
 * nothing — and when even that fails the caller says where the link is instead.
 */

/** Resolves `true` if the text reached the clipboard by either route. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* insecure origin, unfocused document, denied permission — try the fallback */
  }
  return legacyCopy(text);
}

/**
 * The pre-async-clipboard route: a selected off-screen textarea plus `execCommand`.
 * Off-screen rather than `display:none` — a hidden node cannot hold a selection, so the
 * copy would be a no-op. `readonly` keeps the mobile keyboard down.
 */
function legacyCopy(text: string): boolean {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.top = '-1000px';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  try {
    ta.select();
    ta.setSelectionRange(0, text.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    ta.remove();
  }
}
